import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import { Textarea } from "@/components/ui/Textarea";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { Copy, Sparkles, Check, Download, Trash2, ArrowRight } from "lucide-react";
import { copyToClipboard } from "@/lib/utils";

export function SvgMinifier() {
  const [inputCode, setInputCode] = useState(`<svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 100 100" style="enable-background:new 0 0 100 100;" xml:space="preserve">
  <!-- Interactive Design System Element -->
  <style type="text/css">
    .st0 { fill:#10b981; }
    .st1 { fill:#ffffff; }
  </style>
  <g>
    <rect x="15" y="15" class="st0" width="70" height="70" rx="12" />
    <polygon class="st1" points="40,30 70,50 40,70" />
  </g>
</svg>`);
  const [outputCode, setOutputCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState({ original: 0, minified: 0, ratio: 0 });

  // Minifier specific rules
  const [rules, setRules] = useState({
    singleLine: true,
    stripXmlns: false,
    stripComments: true,
    stripVersionAndId: true,
    collapseStyles: true
  });

  const toggleRule = (key: keyof typeof rules) => {
    setRules(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const minifySvg = () => {
    if (!inputCode.trim()) {
      setOutputCode("");
      setStats({ original: 0, minified: 0, ratio: 0 });
      return;
    }

    let minified = inputCode;

    // 1. Remove comments
    if (rules.stripComments) {
      minified = minified.replace(/<!--[\s\S]*?-->/g, "");
    }

    // 2. Remove standard unnecessary XML header wrappers
    minified = minified.replace(/<\?xml[^>]*\?>/gi, "");
    minified = minified.replace(/<!DOCTYPE[^>]*>/gi, "");

    // 3. Strip version/layer ID attributes from outer tag
    if (rules.stripVersionAndId) {
      minified = minified.replace(/\s+version=["'][^"']*["']/gi, "");
      minified = minified.replace(/\s+id=["']Layer_[^"']*["']/gi, "");
      minified = minified.replace(/\s+style=["']enable-background[^"']*["']/gi, "");
    }

    // 4. Strip extra namespaces like XML namespace schema declarations
    if (rules.stripXmlns) {
      minified = minified.replace(/\s+xmlns:xlink=["'][^"']*["']/gi, "");
      minified = minified.replace(/\s+xml:space=["'][^"']*["']/gi, "");
    }

    // 5. Minify inline CSS classes and tags
    if (rules.collapseStyles) {
      minified = minified.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, (match, css) => {
        const cleanCss = css
          .replace(/\s+/g, " ")
          .replace(/\{\s+/g, "{")
          .replace(/\s+\}/g, "}")
          .replace(/;\s+/g, ";")
          .replace(/:\s+/g, ":")
          .trim();
        return `<style>${cleanCss}</style>`;
      });
    }

    // 6. Inline single line collapsing
    if (rules.singleLine) {
      minified = minified.replace(/\s+/g, " ");
      minified = minified.replace(/>\s+</g, "><");
    }

    minified = minified.trim();
    setOutputCode(minified);

    const origBytes = new Blob([inputCode]).size;
    const miniBytes = new Blob([minified]).size;
    const diff = origBytes - miniBytes;
    const ratio = origBytes > 0 ? (diff / origBytes) * 100 : 0;

    setStats({
      original: origBytes,
      minified: miniBytes,
      ratio: Math.max(0, parseFloat(ratio.toFixed(1)))
    });
  };

  useEffect(() => {
    minifySvg();
  }, [inputCode, rules]);

  const handleCopy = () => {
    copyToClipboard(outputCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownload = () => {
    const blob = new Blob([outputCode], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "minified.svg";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <ToolPageLayout
      title="SVG Minifier Online - Minify SVG Code Into Single Line"
      description="Minify, compress, and inline your SVG documents. Strip comments, spaces, XML attributes, and format vector code into a lightweight single-line structure."
      h1="SVG Minifier"
      category="SVG Tools"
      categoryPath="/svg-tools"
      howToUse={[
        "Enter or paste your bloated XML SVG tags inside the input field.",
        "Toggle rules to remove comments, strip schemas, collapse CSS declarations, and compress onto a single line.",
        "Examine the compressed statistics comparing original vs. minified sizes.",
        "Copy your micro-sized, single-line SVG output directly to your clipboard."
      ]}
      faq={[
        { question: "What is an online SVG minifier?", answer: "An online SVG minifier is a tool designed to strip all layout formatting characters (such as tab indents, spaces, carriage returns, and newlines) and redundant XML structures to pack the entire vector code document onto a single line, minimizing file weight." },
        { question: "Why is a single-line minified SVG better for web developers?", answer: "A single-line SVG can be directly embedded inline into HTML or CSS strings with zero spacing bugs, minimizing overall HTML file size and parsing time for web browsers." },
        { question: "Does minifying an SVG alter any path rendering attributes?", answer: "No, minification only alters non-semantic elements like whitespace, comments, and empty indents. All visual shapes, fills, and dimensions are kept 100% identical." },
        { question: "What schema attributes can I safely strip from SVG tags?", answer: "Attributes like 'version', 'xml:space', 'xmlns:xlink', and custom editor 'id' parameters are largely redundant for modern web standards and can be safely stripped without breaking modern browser rendering." },
        { question: "Is this SVG minifier secure to use on local devices?", answer: "Absolutely. All stripping and minification procedures execute strictly in your local browser sandbox without using external servers, ensuring complete client data security." },
        { question: "Can I minify inline CSS styles inside an SVG block?", answer: "Yes. Our tool automatically searches for `<style>` blocks inside the SVG and collapses CSS rules, stripping unnecessary spaces, semi-colons, and line breaks." },
        { question: "What is the difference between an SVG Optimizer and an SVG Minifier?", answer: "An SVG optimizer rounds floating numbers and converts paths/coordinates to simpler forms. An SVG minifier focuses primarily on formatting—stripping whitespace, comments, unnecessary XML wrappers, and creating a compact single-line layout." },
        { question: "Why does standard SVG code contain so many comment lines?", answer: "Graphic design suites like Inkscape, Illustrator, and CorelDraw insert creator logs, software version info, layer descriptions, and licensing headers as comments during exports, which can bloat files." },
        { question: "How do I use minified SVGs inside my React or Next.js app?", answer: "You can copy the single-line minified output and paste it directly into JSX, or save it inside a React Functional Component returning the inline vector." },
        { question: "Will my custom SVG animations still work after minification?", answer: "Yes. CSS transitions, Web Animations, SMIL, and Javascript interactions remain fully functional as all element names, classes, and structures are preserved." }
      ]}
    >
      <div className="space-y-6">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Rules / Side Panel */}
          <div className="space-y-4 bg-white border border-slate-200 p-5 rounded-2xl shadow-xs">
            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-100 text-rose-600">
              <Sparkles className="w-5 h-5" />
              <span className="font-bold text-sm uppercase tracking-wider">Minification Rules</span>
            </div>

            <div className="space-y-3.5">
              <label className="flex items-center gap-3 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rules.singleLine}
                  onChange={() => toggleRule("singleLine")}
                  className="rounded border-slate-300 text-rose-600 focus:ring-rose-500 w-4 h-4"
                />
                Collapse to Single Line (No Spacing)
              </label>

              <label className="flex items-center gap-3 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rules.stripComments}
                  onChange={() => toggleRule("stripComments")}
                  className="rounded border-slate-300 text-rose-600 focus:ring-rose-500 w-4 h-4"
                />
                Strip Developer Comments
              </label>

              <label className="flex items-center gap-3 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rules.stripVersionAndId}
                  onChange={() => toggleRule("stripVersionAndId")}
                  className="rounded border-slate-300 text-rose-600 focus:ring-rose-500 w-4 h-4"
                />
                Strip Outer version & Layer IDs
              </label>

              <label className="flex items-center gap-3 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rules.stripXmlns}
                  onChange={() => toggleRule("stripXmlns")}
                  className="rounded border-slate-300 text-rose-600 focus:ring-rose-500 w-4 h-4"
                />
                Strip xmlns:xlink & xml:space attributes
              </label>

              <label className="flex items-center gap-3 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rules.collapseStyles}
                  onChange={() => toggleRule("collapseStyles")}
                  className="rounded border-slate-300 text-rose-600 focus:ring-rose-500 w-4 h-4"
                />
                Minify & Collapse Inline CSS &lt;style&gt;
              </label>
            </div>

            {/* Statistics */}
            {stats.original > 0 && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2.5 mt-4">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Minified Report</span>
                <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                  <div>Before: <strong className="font-semibold text-slate-800">{stats.original} B</strong></div>
                  <div>After: <strong className="font-semibold text-slate-800">{stats.minified} B</strong></div>
                </div>
                {stats.ratio > 0 && (
                  <div className="bg-emerald-50 text-emerald-800 border border-emerald-100 rounded-lg p-2 text-center text-xs font-bold flex items-center justify-center gap-1">
                    <ArrowRight className="w-3.5 h-3.5" /> Shrunk by {stats.ratio}%
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Editors Grid Column */}
          <div className="md:col-span-2 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Source SVG Markup</Label>
                  <button
                    onClick={() => setInputCode("")}
                    className="text-[10px] text-slate-400 hover:text-red-500 flex items-center gap-1"
                  >
                    <Trash2 className="w-3 H-3" /> Clear Input
                  </button>
                </div>
                <Textarea
                  value={inputCode}
                  onChange={(e) => setInputCode(e.target.value)}
                  className="font-mono text-[10px] leading-relaxed min-h-[250px] bg-slate-50"
                  placeholder="Paste your bloated multi-line SVG tags here..."
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Minified Code (Single Line)</Label>
                <Textarea
                  value={outputCode}
                  readOnly
                  className="font-mono text-[10px] leading-relaxed min-h-[250px] bg-white border-green-200 focus-visible:ring-green-500"
                  placeholder="Compressed single-line output renders automatically..."
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={handleCopy}
                disabled={!outputCode}
                className="flex-1 bg-slate-900 hover:bg-slate-800 text-white gap-2"
              >
                <Copy className="w-4 h-4" />
                {copied ? "Copied Minified Code!" : "Copy Minified Single-Line"}
              </Button>
              <Button
                onClick={handleDownload}
                disabled={!outputCode}
                variant="outline"
                className="gap-2 border-slate-300 text-slate-700"
              >
                <Download className="w-4 h-4" />
                Download Minified
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}
